import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Button, Card, Container, Paragraph, Subheading, Title } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import ProductSetup from './ProductSetup';
import ProductConfiguration from './ProductConfiguration';
import { ReactNode } from 'react';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

export const Header = styled(Box)`
	background: ${(props) => props.theme.colors.lightBlue};
	background-size: cover;
	background-position: center;
	padding-top: 25%;
	position: relative;
`;

const UnderlineLink = styled.a`
	text-decoration: underline;
	:hover {
		text-decoration: none;
	}
`;

export default function ProductLayout(props: {
	id: string;
	type: graphql.ConnectorType;
	description?: ReactNode;
	headerImageURL: string;
	translate: (key: string) => { title: string; description: string } | undefined;
	price?: number;
	pricingLink?: string;
	documentationLink?: string;
}) {
	const { t } = useTranslation();
	const roles = useViewerRoles();

	const query = graphql.useProductQuery();
	const [update] = graphql.useUpdateProductMutation();
	const [queue] = graphql.useQueueConnectorMutation();
	const [queueTwoWay] = graphql.useQueueConnectorTwoWayRetrospectMutation();

	const product: graphql.Connector | undefined = query.data?.viewer.workspace
		.connector as graphql.Connector;

	// Has been selected as primary connector, might or might not need configuration
	const isSelected = product?.type === props.type;

	// Is fully configured and has active synchronisation
	const isActive = isSelected && product?.isActive;

	const isSuperUser = roles.isWorkspaceSuperuser;

	return (
		<Container py={4}>
			<Header
				mb={3}
				style={{
					backgroundImage: `url('${props.headerImageURL}')`,
				}}
			>
				{query.data && !product && (
					<Flex justifyContent="flex-end" style={{ position: 'absolute', top: 10, right: 10 }}>
						<Card>
							<Subheading textAlign="right">Kosten</Subheading>

							<Paragraph>
								{props.pricingLink && (
									<>
										Licentie:{' '}
										<UnderlineLink href={props.pricingLink} target="_blank" rel="noopener">
											bekijk website
										</UnderlineLink>
										<br />
									</>
								)}
								Integratie:{' '}
								{props.price !== undefined
									? props.price > 0
										? t('settings.connectors.marketplace.price.month', { price: props.price })
										: t('settings.connectors.marketplace.price.free')
									: t('settings.connectors.marketplace.price.month', { price: '125' })}
							</Paragraph>
						</Card>
					</Flex>
				)}
			</Header>
			<Flex mb={3} alignItems="center">
				<Title flexGrow={1} flexShrink={1}>
					{t(`connectors.product.${props.type.toLowerCase()}.title`)}
				</Title>

				{query.data && product && isSelected && (
					<Box ml={2} flexGrow={0} flexShrink={0}>
						<Flex flexDirection="row">
							{isSuperUser && (
								<>
									<Button
										type="secondary"
										title={t('connectors.settings.configuration.queue')}
										onClick={async () => {
											await queue();
										}}
										mr={2}
									/>
									<Button
										type="secondary"
										title={t('connectors.settings.configuration.queueTwoWay')}
										onClick={async () => {
											await queueTwoWay();
										}}
										mr={2}
									/>
								</>
							)}
							{isActive ? (
								<Button
									type="destructive"
									title={t('connectors.settings.configuration.deactivate')}
									onClick={async () => {
										await update({ variables: { input: { isActive: false } } });
									}}
								/>
							) : (
								<Button
									type="primary"
									title={t('connectors.settings.configuration.activate')}
									onClick={async () => {
										await update({ variables: { input: { isActive: true } } });
									}}
									disabled={product.config.some((field) => !field.isConfigured)}
								/>
							)}
						</Flex>
					</Box>
				)}
			</Flex>
			{query.data ? (
				isSelected ? (
					<ProductConfiguration
						connector={product}
						translate={props.translate}
						documentationLink={props.documentationLink}
					/>
				) : (
					<ProductSetup
						type={props.type}
						description={
							props.description ?? (
								<Box mb={3}>
									<Paragraph>De beschrijving wordt binnenkort toegevoegd.</Paragraph>
								</Box>
							)
						}
						translate={props.translate}
						price={props.price}
						available={!product}
						documentationLink={props.documentationLink}
					/>
				)
			) : (
				<ProductSetup.Skeleton />
			)}
		</Container>
	);
}
